import { authService } from './auth';

/**
 * Authenticated fetch wrapper that automatically adds authorization headers
 * This is the single source of truth for making authenticated API calls
 * 
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns Promise<Response>
 */
export async function authenticatedFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  try {
    // Get the current session through authService
    // This is now the ONLY place where we get session for API calls
    const session = await authService.getSessionForAPI();
    
    if (!session?.access_token) {
      console.error('❌ [authenticatedFetch] No valid session available');
      // Throw a specific error that can be caught and handled appropriately
      const error = new Error('Authentication required');
      (error as any).code = 'AUTH_REQUIRED';
      throw error;
    }

    console.log(`🔐 [authenticatedFetch] Making authenticated request to: ${url}`);
    
    // Merge the Authorization header with any existing headers
    const headers = new Headers(options?.headers);
    headers.set('Authorization', `Bearer ${session.access_token}`);
    
    // Make the request with the authentication header
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    // Handle 401 Unauthorized responses
    if (response.status === 401) {
      console.error('❌ [authenticatedFetch] Received 401 Unauthorized');
      const error = new Error('Session expired');
      (error as any).code = 'SESSION_EXPIRED';
      throw error;
    }
    
    return response;
  } catch (error) {
    console.error('❌ [authenticatedFetch] Request failed:', error);
    // Re-throw the error for the caller to handle
    throw error;
  }
}

/**
 * Convenience wrapper for authenticated JSON requests
 * Automatically sets Content-Type and parses JSON response
 */
export async function authenticatedJSONFetch<T = any>(
  url: string,
  options?: RequestInit
): Promise<{ ok: boolean; data?: T; error?: any }> {
  try {
    const headers = new Headers(options?.headers);
    if (options?.body && typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
    
    const response = await authenticatedFetch(url, {
      ...options,
      headers
    });
    
    // 某些后端在 204 或无 body 场景
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    
    if (!response.ok) {
      return { ok: false, error: data };
    }
    
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error };
  }
}

// -------- Phase A async readings helpers --------
export async function createReadingJob(payload: {
  birth_date: string;
  birth_time?: string;
  birth_location?: { country?: string; city?: string; latitude?: number; longitude?: number };
  timezone?: string;
  gender?: 'male'|'female'|'unknown';
  language?: 'zh'|'en';
}) {
  const location = [payload.birth_location?.city || '', payload.birth_location?.country || '']
    .filter(Boolean)
    .join(', ');
  const body = {
    birthInfo: {
      date: payload.birth_date,
      time: payload.birth_time ?? '00:00',
      location,
      timezone: payload.timezone || 'UTC',
      gender: payload.gender || 'unknown'
    }
  };
  return authenticatedJSONFetch<{ success: boolean; data?: { jobId?: string; job_id?: string; status?: string; estimatedTime?: number } }>(
    '/api/analyses',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }
  );
}

export async function getReadingJobStatus(jobId: string) {
  return authenticatedJSONFetch<{ success: boolean; data?: { status?: string; progress?: number; preview?: any; error?: any } }>(
    `/api/analyses/${jobId}/status`
  );
}

export async function getReadingResult(jobId: string) {
  return authenticatedJSONFetch<{ success: boolean; data?: { result?: any } }>(
    `/api/analyses/${jobId}/result`
  );
}

export async function triggerAIGeneration(jobId: string) {
  return authenticatedJSONFetch<{ success: boolean; message?: string }>(
    `/api/analyses/${jobId}/generate-ai`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }
  );
}

export async function unlockPremiumContent(jobId: string, contentType: string = 'full_report') {
  return authenticatedJSONFetch<{ success: boolean; data?: { unlock_id?: string; status?: string } }>(
    `/api/analyses/${jobId}/premium/unlock`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_type: contentType })
    }
  );
}

export async function retryPremiumContent(jobId: string, contentType: string = 'full_report') {
  return authenticatedJSONFetch<{ success: boolean; data?: { status?: string } }>(
    `/api/analyses/${jobId}/premium/retry`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_type: contentType })
    }
  );
}

/**
 * Phase 3: 重试单个失败的段
 * @param jobId - 分析任务 ID
 * @param segmentKey - 段键名 (career, relationships, health, wealth, environment, timeline)
 */
export async function retrySegment(jobId: string, segmentKey: string) {
  return authenticatedJSONFetch<{
    success: boolean;
    data?: {
      status?: string;
      segment_key?: string;
      message?: string;
    }
  }>(
    `/api/analyses/${jobId}/premium/segments/${segmentKey}/retry`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }
  );
}

// -------- Legacy analyses helpers --------
export async function getLegacyAnalysisStatus(jobId: string) {
  return authenticatedJSONFetch<{
    success: boolean;
    data?: {
      status?: string;
      progress?: number;
      preview?: any;
      readingId?: string;
      reading_id?: string;
      error?: any;
      error_code?: string;
      message?: string;
      step?: string;
    };
  }>(`/api/analyses/${jobId}/status`);
}

export async function getLegacyAnalysisResult(jobId: string) {
  return authenticatedJSONFetch<{
    success: boolean;
    data?: {
      result?: any;
    };
  }>(`/api/analyses/${jobId}/result`);
}
